
import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';

export default function ResetPasswordPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  
  // Password form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const handlePasswordReset = async () => {
      if (!supabase) {
        setError('Authentication service not available');
        setIsLoading(false);
        return;
      }

      console.log('[PASSWORD RESET] Starting password reset verification...');
      console.log('[PASSWORD RESET] Current URL:', window.location.href);

      try {
        // Step 1: Clear any existing session first
        console.log('[PASSWORD RESET] Clearing existing session...');
        await supabase.auth.signOut();

        // Step 2: Check for PKCE authorization code (modern flow)
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        
        console.log('[PASSWORD RESET] Checking for authorization code:', !!authCode);

        if (authCode) {
          console.log('[PASSWORD RESET] Processing PKCE authorization code...');
          
          try {
            const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(authCode);
            
            if (exchangeError) {
              console.error('[PASSWORD RESET] Code exchange failed:', exchangeError);
              throw new Error('Invalid or expired reset link. Please request a new one.');
            }
            
            if (data.session && data.user) {
              console.log('[PASSWORD RESET] PKCE session established successfully');
              setIsAuthenticated(true);
              setIsLoading(false);
              
              // Clean URL for better UX
              const cleanUrl = `${window.location.origin}${window.location.pathname}`;
              window.history.replaceState({}, document.title, cleanUrl);
              return;
            } else {
              throw new Error('Failed to establish session from reset link.');
            }
          } catch (codeError) {
            console.error('[PASSWORD RESET] PKCE flow error:', codeError);
            throw new Error('Invalid or expired reset link. Please request a new one.');
          }
        }

        // Step 2.5: Check for token-based recovery (query parameter)
        const token = urlParams.get('token');
        const type = urlParams.get('type');

        console.log('[PASSWORD RESET] Checking for token parameter:', !!token, 'Type:', type);

        if (token && type === 'recovery') {
          console.log('[PASSWORD RESET] Processing token-based recovery...');
          
          try {
            const { data, error: verifyError } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'recovery'
            });
            
            if (verifyError) {
              console.error('[PASSWORD RESET] Token verification failed:', verifyError);
              throw new Error('Invalid or expired reset link. Please request a new one.');
            }
            
            if (data.session && data.user) {
              console.log('[PASSWORD RESET] Token verification successful');
              setIsAuthenticated(true);
              setIsLoading(false);
              
              // Clean URL for better UX
              const cleanUrl = `${window.location.origin}${window.location.pathname}`;
              window.history.replaceState({}, document.title, cleanUrl);
              return;
            } else {
              throw new Error('Failed to establish session from reset token.');
            }
          } catch (tokenError) {
            console.error('[PASSWORD RESET] Token flow error:', tokenError);
            throw new Error('Invalid or expired reset link. Please request a new one.');
          }
        }

        // Step 3: Check for implicit flow tokens (legacy support)
        const hashFragment = window.location.hash;
        console.log('[PASSWORD RESET] Checking hash fragment:', !!hashFragment);

        if (hashFragment && hashFragment.includes('access_token')) {
          console.log('[PASSWORD RESET] Processing implicit flow tokens...');
          
          const hashParams = new URLSearchParams(hashFragment.substring(1));
          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const tokenType = hashParams.get('type');
          
          console.log('[PASSWORD RESET] Token type:', tokenType, 'Has access token:', !!accessToken);
          
          if (tokenType === 'recovery' && accessToken) {
            try {
              const { data, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken || ''
              });
              
              if (sessionError) {
                console.error('[PASSWORD RESET] Session error:', sessionError);
                throw new Error('Invalid or expired reset link. Please request a new one.');
              }
              
              if (data.session && data.user) {
                console.log('[PASSWORD RESET] Implicit flow session established successfully');
                setIsAuthenticated(true);
                setIsLoading(false);
                
                // Clean URL for better UX
                const cleanUrl = `${window.location.origin}${window.location.pathname}`;
                window.history.replaceState({}, document.title, cleanUrl);
                return;
              } else {
                throw new Error('Failed to establish session from reset tokens.');
              }
            } catch (sessionError) {
              console.error('[PASSWORD RESET] Implicit flow error:', sessionError);
              throw new Error('Invalid or expired reset link. Please request a new one.');
            }
          }
        }

        // Step 4: Check if user is already authenticated (edge case)
        console.log('[PASSWORD RESET] Checking for existing session...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (session && !sessionError) {
          console.log('[PASSWORD RESET] Found existing valid session');
          setIsAuthenticated(true);
          setIsLoading(false);
          return;
        }

        // Step 5: No valid reset credentials found
        console.log('[PASSWORD RESET] No valid reset credentials found');
        throw new Error('Invalid or missing reset link. Please request a new password reset.');

      } catch (err: any) {
        console.error('[PASSWORD RESET] Error during verification:', err);
        setError(err.message || 'Invalid or expired reset link. Please request a new one.');
        setIsLoading(false);
      }
    };

    // Add a small delay to ensure DOM is ready and prevent race conditions
    const timer = setTimeout(() => {
      handlePasswordReset();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle password reset submission with enhanced validation
  const handlePasswordReset = async () => {
    // Enhanced password validation
    if (!newPassword || !confirmPassword) {
      setPasswordError('Please enter and confirm your new password');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Additional password strength validation
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumbers = /\d/.test(newPassword);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setPasswordError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }

    if (!supabase) {
      setPasswordError('Authentication service not available');
      return;
    }

    setIsResettingPassword(true);
    setPasswordError('');

    try {
      console.log('[PASSWORD RESET] Updating password...');
      
      const { error: updateError } = await supabase.auth.updateUser({ 
        password: newPassword 
      });

      if (updateError) {
        throw updateError;
      }

      console.log('[PASSWORD RESET] Password updated successfully');
      setPasswordResetSuccess(true);
      
      toast({
        title: 'Password Reset Successful',
        description: 'Your password has been updated successfully. Redirecting...',
      });

      // Sign out user after password reset for security
      await supabase.auth.signOut();

      // Redirect to login page after successful reset
      setTimeout(() => {
        navigate('/auth?tab=login&message=password-reset-success');
      }, 2000);

    } catch (error: any) {
      console.error('[PASSWORD RESET] Update error:', error);
      
      // Handle specific Supabase errors
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.message?.includes('session_not_found')) {
        errorMessage = 'Session expired. Please request a new reset link.';
      } else if (error.message?.includes('weak_password')) {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setPasswordError(errorMessage);
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Success state - password reset complete
  if (passwordResetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle className="text-xl font-semibold">Password Reset Successful!</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-gray-600 dark:text-gray-400">
              Your password has been successfully updated. You can now sign in with your new password.
            </p>
            <p className="text-center text-sm text-gray-500 dark:text-gray-500">
              Redirecting to login page...
            </p>
            <Button 
              onClick={() => navigate('/auth?tab=login')} 
              className="w-full"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show password reset form when authenticated
  if (isAuthenticated && !passwordResetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-primary" />
            <CardTitle className="text-2xl font-bold">Reset Your Password</CardTitle>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Enter your new password below
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordError('');
                    }}
                    placeholder="Enter your new password"
                    disabled={isResettingPassword}
                    className="pr-10"
                    data-testid="input-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isResettingPassword}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Password must be at least 8 characters with uppercase, lowercase, and numbers
                </p>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError('');
                    }}
                    placeholder="Confirm your new password"
                    disabled={isResettingPassword}
                    className="pr-10"
                    data-testid="input-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isResettingPassword}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {passwordError && (
                <Alert variant="destructive">
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}

              <Button
                onClick={handlePasswordReset}
                disabled={isResettingPassword || !newPassword || !confirmPassword}
                className="w-full"
                data-testid="button-reset-password"
              >
                {isResettingPassword ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    Resetting Password...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state while processing reset link
  if (isLoading && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="animate-spin h-8 w-8 text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Verifying reset link...</p>
              <p className="text-xs text-gray-500 mt-2">This may take a few moments</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state for invalid or expired reset links
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-semibold text-red-600">Password Reset Failed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Button 
                className="w-full" 
                onClick={() => navigate('/forgot-password')} 
                data-testid="button-request-new-link"
              >
                Request New Reset Link
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/auth?tab=login')} 
                data-testid="button-back-to-login"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl font-semibold">Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <Loader2 className="animate-spin h-8 w-8 text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Please wait...</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
